import { lazy, Suspense, type ReactNode } from 'react'
import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router-dom'

import { AppShell } from '@/components/layout/AppShell'
import { Skeleton } from '@/components/ui/Skeleton'
import type { PermissionKey } from '@/lib/permissions'
import { Permission } from '@/lib/permissions'
import { ForbiddenPage, NotFoundPage, RouteErrorPage } from '@/pages/Errors'
import { LoginPage } from '@/pages/Login'
import { useAuthStore } from '@/stores/auth'

const DashboardPage = lazy(() =>
  import('@/pages/Dashboard').then((m) => ({ default: m.DashboardPage })),
)
const TicketsPage = lazy(() =>
  import('@/pages/tickets/TicketList').then((m) => ({ default: m.TicketListPage })),
)
const BugsPage = lazy(() => import('@/pages/bugs/Bugs').then((m) => ({ default: m.BugsPage })))
const RoadmapPage = lazy(() =>
  import('@/pages/roadmap/Roadmap').then((m) => ({ default: m.RoadmapPage })),
)
const StatusPagePage = lazy(() =>
  import('@/pages/statuspage/StatusPage').then((m) => ({ default: m.StatusPagePage })),
)
const BetaTestPage = lazy(() =>
  import('@/pages/betatest/BetaTest').then((m) => ({ default: m.BetaTestPage })),
)
const WikiConsolePage = lazy(() =>
  import('@/pages/wiki/Wiki').then((m) => ({ default: m.WikiConsolePage })),
)
const BugDetailPage = lazy(() =>
  import('@/pages/bugs/BugDetail').then((m) => ({ default: m.BugDetailPage })),
)
const TicketDetailPage = lazy(() =>
  import('@/pages/tickets/TicketDetail').then((m) => ({ default: m.TicketDetailPage })),
)
const TicketCategoriesPage = lazy(() =>
  import('@/pages/tickets/Categories').then((m) => ({ default: m.TicketCategoriesPage })),
)
const TicketFormsPage = lazy(() =>
  import('@/pages/tickets/Forms').then((m) => ({ default: m.TicketFormsPage })),
)
const TicketPanelsPage = lazy(() =>
  import('@/pages/tickets/Panels').then((m) => ({ default: m.TicketPanelsPage })),
)
const MembersPage = lazy(() =>
  import('@/pages/members/MemberList').then((m) => ({ default: m.MemberListPage })),
)
const MemberDetailPage = lazy(() =>
  import('@/pages/members/MemberDetail').then((m) => ({ default: m.MemberDetailPage })),
)
const ModerationQueuePage = lazy(() =>
  import('@/pages/moderation/Queue').then((m) => ({ default: m.ModerationQueuePage })),
)
const ModerationRulesPage = lazy(() =>
  import('@/pages/moderation/Rules').then((m) => ({ default: m.ModerationRulesPage })),
)
const DisciplineRegisterPage = lazy(() =>
  import('@/pages/discipline/Register').then((m) => ({ default: m.DisciplineRegisterPage })),
)
const SanctionsPage = lazy(() =>
  import('@/pages/discipline/Sanctions').then((m) => ({ default: m.SanctionsPage })),
)
const AppealsPage = lazy(() =>
  import('@/pages/discipline/Appeals').then((m) => ({ default: m.AppealsPage })),
)
const DisciplineCataloguePage = lazy(() =>
  import('@/pages/discipline/Catalogue').then((m) => ({ default: m.DisciplineCataloguePage })),
)
const DisciplineSettingsPage = lazy(() =>
  import('@/pages/settings/Discipline').then((m) => ({ default: m.DisciplineSettingsPage })),
)
const RuleBookPage = lazy(() => import('@/pages/rules/RuleBook'))
const StaffRuleBookPage = lazy(() => import('@/pages/rules/StaffRuleBook'))
const WhitelistListPage = lazy(() =>
  import('@/pages/whitelist/WhitelistList').then((m) => ({ default: m.WhitelistListPage })),
)
const WhitelistDetailPage = lazy(() =>
  import('@/pages/whitelist/WhitelistDetail').then((m) => ({ default: m.WhitelistDetailPage })),
)
const ApplicationListPage = lazy(() =>
  import('@/pages/applications/ApplicationList').then((m) => ({ default: m.ApplicationListPage })),
)
const ApplicationDetailPage = lazy(() =>
  import('@/pages/applications/ApplicationDetail').then((m) => ({
    default: m.ApplicationDetailPage,
  })),
)
const ApplicationPositionsPage = lazy(() =>
  import('@/pages/applications/Positions').then((m) => ({
    default: m.ApplicationPositionsPage,
  })),
)
const GiveawayListPage = lazy(() =>
  import('@/pages/giveaways/GiveawayList').then((m) => ({ default: m.GiveawayListPage })),
)
const GiveawayDetailPage = lazy(() =>
  import('@/pages/giveaways/GiveawayDetail').then((m) => ({ default: m.GiveawayDetailPage })),
)
const PollListPage = lazy(() =>
  import('@/pages/polls/PollList').then((m) => ({ default: m.PollListPage })),
)
const PollDetailPage = lazy(() =>
  import('@/pages/polls/PollDetail').then((m) => ({ default: m.PollDetailPage })),
)
const VehicleListPage = lazy(() =>
  import('@/pages/vehicles/VehicleList').then((m) => ({ default: m.VehicleListPage })),
)
const VehicleDetailPage = lazy(() =>
  import('@/pages/vehicles/VehicleDetail').then((m) => ({ default: m.VehicleDetailPage })),
)
const VehicleSetupPage = lazy(() =>
  import('@/pages/vehicles/Setup').then((m) => ({ default: m.VehicleSetupPage })),
)
const VehiclePanelPage = lazy(() =>
  import('@/pages/vehicles/Panel').then((m) => ({ default: m.VehiclePanelPage })),
)
const FuelPage = lazy(() => import('@/pages/vehicles/Fuel').then((m) => ({ default: m.FuelPage })))
const GasStationsPage = lazy(() =>
  import('@/pages/vehicles/Stations').then((m) => ({ default: m.GasStationsPage })),
)
const DealershipsPage = lazy(() =>
  import('@/pages/vehicles/Dealerships').then((m) => ({ default: m.DealershipsPage })),
)
const WorkshopsPage = lazy(() =>
  import('@/pages/vehicles/Workshops').then((m) => ({ default: m.WorkshopsPage })),
)
const GaragesPage = lazy(() =>
  import('@/pages/vehicles/Garages').then((m) => ({ default: m.GaragesPage })),
)
const GarageDetailPage = lazy(() =>
  import('@/pages/vehicles/GarageDetail').then((m) => ({ default: m.GarageDetailPage })),
)
const RentalsPage = lazy(() =>
  import('@/pages/rentals/Rentals').then((m) => ({ default: m.RentalsPage })),
)
const CarWashPage = lazy(() =>
  import('@/pages/carwash/CarWash').then((m) => ({ default: m.CarWashPage })),
)
const PlatesPage = lazy(() =>
  import('@/pages/plates/Plates').then((m) => ({ default: m.PlatesPage })),
)
const CharacterListPage = lazy(() =>
  import('@/pages/characters/CharacterList').then((m) => ({ default: m.CharacterListPage })),
)
const CharacterDetailPage = lazy(() =>
  import('@/pages/characters/CharacterDetail').then((m) => ({ default: m.CharacterDetailPage })),
)
const InventoryPage = lazy(() =>
  import('@/pages/inventory/Inventory').then((m) => ({ default: m.InventoryPage })),
)
const WeaponListPage = lazy(() =>
  import('@/pages/weapons/WeaponList').then((m) => ({ default: m.WeaponListPage })),
)
const WeaponDetailPage = lazy(() =>
  import('@/pages/weapons/WeaponDetail').then((m) => ({ default: m.WeaponDetailPage })),
)
const BusinessesPage = lazy(() =>
  import('@/pages/businesses/Businesses').then((m) => ({ default: m.BusinessesPage })),
)
const BusinessDetailPage = lazy(() =>
  import('@/pages/businesses/BusinessDetail').then((m) => ({ default: m.BusinessDetailPage })),
)
const PropertiesPage = lazy(() =>
  import('@/pages/housing/Properties').then((m) => ({ default: m.PropertiesPage })),
)
const PropertyDetailPage = lazy(() =>
  import('@/pages/housing/PropertyDetail').then((m) => ({ default: m.PropertyDetailPage })),
)
const HousingFurniturePage = lazy(() =>
  import('@/pages/housing/Furniture').then((m) => ({ default: m.FurniturePage })),
)
const HousingInteriorsPage = lazy(() =>
  import('@/pages/housing/Interiors').then((m) => ({ default: m.InteriorsPage })),
)
const HousingSettingsPage = lazy(() =>
  import('@/pages/housing/HousingSettings').then((m) => ({ default: m.HousingSettingsPage })),
)
const ShellsPage = lazy(() =>
  import('@/pages/shells/Shells').then((m) => ({ default: m.ShellsPage })),
)
const ShellDetailPage = lazy(() =>
  import('@/pages/shells/ShellDetail').then((m) => ({ default: m.ShellDetailPage })),
)
const MdtPage = lazy(() => import('@/pages/mdt/Mdt').then((m) => ({ default: m.MdtPage })))
const JailPage = lazy(() => import('@/pages/jail/Jail').then((m) => ({ default: m.JailPage })))
const RobberiesPage = lazy(() =>
  import('@/pages/robberies/Robberies').then((m) => ({ default: m.RobberiesPage })),
)
const DispatchPage = lazy(() =>
  import('@/pages/dispatch/Dispatch').then((m) => ({ default: m.DispatchPage })),
)
const DomainsPage = lazy(() =>
  import('@/pages/domains/Domains').then((m) => ({ default: m.DomainsPage })),
)
const DomainSetupPage = lazy(() =>
  import('@/pages/domains/Setup').then((m) => ({ default: m.DomainSetupPage })),
)
const HostingPage = lazy(() =>
  import('@/pages/domains/Hosting').then((m) => ({ default: m.HostingPage })),
)
const PhonesPage = lazy(() =>
  import('@/pages/phones/Phones').then((m) => ({ default: m.PhonesPage })),
)
const PhoneDetailPage = lazy(() =>
  import('@/pages/phones/PhoneDetail').then((m) => ({ default: m.PhoneDetailPage })),
)
const PhoneCallsPage = lazy(() =>
  import('@/pages/phones/Calls').then((m) => ({ default: m.PhoneCallsPage })),
)
const PhoneMediaPage = lazy(() =>
  import('@/pages/phones/Media').then((m) => ({ default: m.PhoneMediaPage })),
)
const TabletsPage = lazy(() =>
  import('@/pages/tablets/Tablets').then((m) => ({ default: m.TabletsPage })),
)
const TabletDetailPage = lazy(() =>
  import('@/pages/tablets/TabletDetail').then((m) => ({ default: m.TabletDetailPage })),
)
const TabletMediaPage = lazy(() =>
  import('@/pages/tablets/Media').then((m) => ({ default: m.TabletMediaPage })),
)
const MailPage = lazy(() => import('@/pages/mail/Mail').then((m) => ({ default: m.MailPage })))
const MailAccountPage = lazy(() =>
  import('@/pages/mail/Account').then((m) => ({ default: m.MailAccountPage })),
)
const MailServicePage = lazy(() =>
  import('@/pages/mail/Service').then((m) => ({ default: m.MailServicePage })),
)
const MailSetupPage = lazy(() =>
  import('@/pages/mail/Setup').then((m) => ({ default: m.MailSetupPage })),
)
const CamerasPage = lazy(() =>
  import('@/pages/cctv/Cameras').then((m) => ({ default: m.CamerasPage })),
)
const SpeedCamerasPage = lazy(() =>
  import('@/pages/speedcameras/SpeedCameras').then((m) => ({ default: m.SpeedCamerasPage })),
)
const ScootersPage = lazy(() =>
  import('@/pages/scooters/Scooters').then((m) => ({ default: m.ScootersPage })),
)
const DoorsPage = lazy(() => import('@/pages/doors/Doors').then((m) => ({ default: m.DoorsPage })))
const DrivingSchoolPage = lazy(() =>
  import('@/pages/drivingschool/DrivingSchool').then((m) => ({ default: m.DrivingSchoolPage })),
)
const NewsPage = lazy(() => import('@/pages/news/News').then((m) => ({ default: m.NewsPage })))
const ArticleEditorPage = lazy(() =>
  import('@/pages/news/ArticleEditor').then((m) => ({ default: m.ArticleEditorPage })),
)
const InstashotPage = lazy(() =>
  import('@/pages/instashot/Instashot').then((m) => ({ default: m.InstashotPage })),
)
const ChirpPage = lazy(() => import('@/pages/chirp/Chirp').then((m) => ({ default: m.ChirpPage })))
const StationsPage = lazy(() =>
  import('@/pages/stations/Stations').then((m) => ({ default: m.StationsPage })),
)
const GraffitiPage = lazy(() =>
  import('@/pages/graffiti/Graffiti').then((m) => ({ default: m.GraffitiPage })),
)
const LedPanelsPage = lazy(() =>
  import('@/pages/ledpanels/LedPanels').then((m) => ({ default: m.LedPanelsPage })),
)
const PingPongPage = lazy(() =>
  import('@/pages/pingpong/PingPong').then((m) => ({ default: m.PingPongPage })),
)
const DrugsPage = lazy(() => import('@/pages/drugs/Drugs').then((m) => ({ default: m.DrugsPage })))
const RacesPage = lazy(() => import('@/pages/races/Races').then((m) => ({ default: m.RacesPage })))
const EmotesPage = lazy(() =>
  import('@/pages/emotes/Emotes').then((m) => ({ default: m.EmotesPage })),
)
const ShopsPage = lazy(() => import('@/pages/shops/Shops').then((m) => ({ default: m.ShopsPage })))
const PawnPage = lazy(() => import('@/pages/pawn/Pawn').then((m) => ({ default: m.PawnPage })))
const StylePage = lazy(() => import('@/pages/style/Style').then((m) => ({ default: m.StylePage })))
const FirearmsPage = lazy(() =>
  import('@/pages/firearms/Firearms').then((m) => ({ default: m.FirearmsPage })),
)
const InsurancePage = lazy(() =>
  import('@/pages/insurance/Insurance').then((m) => ({ default: m.InsurancePage })),
)
const ImpoundPage = lazy(() =>
  import('@/pages/impound/Impound').then((m) => ({ default: m.ImpoundPage })),
)
const VendingPage = lazy(() =>
  import('@/pages/vending/Vending').then((m) => ({ default: m.VendingPage })),
)
const CraftingPage = lazy(() =>
  import('@/pages/crafting/Crafting').then((m) => ({ default: m.CraftingPage })),
)
const ChessPage = lazy(() => import('@/pages/chess/Chess').then((m) => ({ default: m.ChessPage })))
const BilliardsPage = lazy(() =>
  import('@/pages/billiards/Billiards').then((m) => ({ default: m.BilliardsPage })),
)
const DesktopsPage = lazy(() =>
  import('@/pages/desktops/Desktops').then((m) => ({ default: m.DesktopsPage })),
)
const DirectoryPage = lazy(() =>
  import('@/pages/directory/Directory').then((m) => ({ default: m.DirectoryPage })),
)
const DatacenterPage = lazy(() =>
  import('@/pages/datacenter/Datacenter').then((m) => ({ default: m.DatacenterPage })),
)
const GrowPage = lazy(() => import('@/pages/grow/Grow').then((m) => ({ default: m.GrowPage })))
const MedicalPage = lazy(() =>
  import('@/pages/medical/Medical').then((m) => ({ default: m.MedicalPage })),
)
const TowersPage = lazy(() =>
  import('@/pages/network/Towers').then((m) => ({ default: m.TowersPage })),
)
const PowerPage = lazy(() => import('@/pages/power/Power').then((m) => ({ default: m.PowerPage })))
const BusinessRegistrationsPage = lazy(() =>
  import('@/pages/businesses/Registrations').then((m) => ({
    default: m.BusinessRegistrationsPage,
  })),
)
const BankingOverviewPage = lazy(() =>
  import('@/pages/banking/Overview').then((m) => ({ default: m.BankingOverviewPage })),
)
const BankAccountsPage = lazy(() =>
  import('@/pages/banking/Accounts').then((m) => ({ default: m.BankAccountsPage })),
)
const BankAccountDetailPage = lazy(() =>
  import('@/pages/banking/AccountDetail').then((m) => ({ default: m.BankAccountDetailPage })),
)
const BankLedgerPage = lazy(() =>
  import('@/pages/banking/Ledger').then((m) => ({ default: m.BankLedgerPage })),
)
const BankLoansPage = lazy(() =>
  import('@/pages/banking/Loans').then((m) => ({ default: m.BankLoansPage })),
)
const SkimmersPage = lazy(() =>
  import('@/pages/banking/Skimmers').then((m) => ({ default: m.SkimmersPage })),
)
const BankLabelsPage = lazy(() =>
  import('@/pages/banking/Labels').then((m) => ({ default: m.BankLabelsPage })),
)
const BanksPage = lazy(() =>
  import('@/pages/banking/Banks').then((m) => ({ default: m.BanksPage })),
)
const QuestsPage = lazy(() =>
  import('@/pages/quests/Quests').then((m) => ({ default: m.QuestsPage })),
)
const QuestAssignmentsPage = lazy(() =>
  import('@/pages/quests/Assignments').then((m) => ({ default: m.QuestAssignmentsPage })),
)
const BattlePassSeasonsPage = lazy(() =>
  import('@/pages/battlepass/Seasons').then((m) => ({ default: m.BattlePassSeasonsPage })),
)
const BattlePassSeasonEditorPage = lazy(() =>
  import('@/pages/battlepass/SeasonEditor').then((m) => ({ default: m.SeasonEditorPage })),
)
const BattlePassPassesPage = lazy(() =>
  import('@/pages/battlepass/Passes').then((m) => ({ default: m.BattlePassPassesPage })),
)
const BattlePassPaymentsPage = lazy(() =>
  import('@/pages/battlepass/Payments').then((m) => ({ default: m.BattlePassPaymentsPage })),
)
const StatusPage = lazy(() =>
  import('@/pages/status/Status').then((m) => ({ default: m.StatusPage })),
)
const SkillsPage = lazy(() =>
  import('@/pages/skills/Skills').then((m) => ({ default: m.SkillsPage })),
)
const TalentsPage = lazy(() =>
  import('@/pages/talents/Talents').then((m) => ({ default: m.TalentsPage })),
)
const WeatherPage = lazy(() =>
  import('@/pages/weather/Weather').then((m) => ({ default: m.WeatherPage })),
)
const NpcPage = lazy(() => import('@/pages/npc/Npc').then((m) => ({ default: m.NpcPage })))
const GameChatPage = lazy(() =>
  import('@/pages/chat/GameChat').then((m) => ({ default: m.GameChatPage })),
)
const LiveMapPage = lazy(() =>
  import('@/pages/live-map/LiveMap').then((m) => ({ default: m.LiveMapPage })),
)
const WhitelistSettingsPage = lazy(() =>
  import('@/pages/settings/Whitelist').then((m) => ({ default: m.WhitelistSettingsPage })),
)
const VoicePage = lazy(() => import('@/pages/Voice').then((m) => ({ default: m.VoicePage })))
const DiscordArchitectPage = lazy(() =>
  import('@/pages/discord-ai/Architect').then((m) => ({ default: m.DiscordArchitectPage })),
)
const StatsChannelsPage = lazy(() =>
  import('@/pages/StatsChannels').then((m) => ({ default: m.StatsChannelsPage })),
)
const StaffPanelPage = lazy(() =>
  import('@/pages/StaffPanel').then((m) => ({ default: m.StaffPanelPage })),
)
const AbsencesPage = lazy(() =>
  import('@/pages/absences/Absences').then((m) => ({ default: m.AbsencesPage })),
)
const AbsencePanelPage = lazy(() =>
  import('@/pages/absences/Panel').then((m) => ({ default: m.AbsencePanelPage })),
)
const ScheduledMessagesPage = lazy(() =>
  import('@/pages/ScheduledMessages').then((m) => ({ default: m.ScheduledMessagesPage })),
)
const SettingsLayout = lazy(() =>
  import('@/pages/settings/SettingsLayout').then((m) => ({ default: m.SettingsLayout })),
)
const GeneralSettingsPage = lazy(() =>
  import('@/pages/settings/General').then((m) => ({ default: m.GeneralSettingsPage })),
)
const WelcomeSettingsPage = lazy(() =>
  import('@/pages/settings/Welcome').then((m) => ({ default: m.WelcomeSettingsPage })),
)
const ModerationSettingsPage = lazy(() =>
  import('@/pages/settings/Moderation').then((m) => ({ default: m.ModerationSettingsPage })),
)
const NicknameSettingsPage = lazy(() =>
  import('@/pages/settings/Nicknames').then((m) => ({ default: m.NicknameSettingsPage })),
)
const RoleMemorySettingsPage = lazy(() =>
  import('@/pages/settings/RoleMemory').then((m) => ({ default: m.RoleMemorySettingsPage })),
)
const ArrivalSettingsPage = lazy(() =>
  import('@/pages/settings/Arrival').then((m) => ({ default: m.ArrivalSettingsPage })),
)
const PermissionsPage = lazy(() =>
  import('@/pages/settings/Permissions').then((m) => ({ default: m.PermissionsPage })),
)
const BotPage = lazy(() => import('@/pages/Bot').then((m) => ({ default: m.BotPage })))
const AuditPage = lazy(() => import('@/pages/Audit').then((m) => ({ default: m.AuditPage })))
const ActivityPage = lazy(() =>
  import('@/pages/Activity').then((m) => ({ default: m.ActivityPage })),
)
const AccountPage = lazy(() => import('@/pages/Account').then((m) => ({ default: m.AccountPage })))

function PageFallback() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-4 w-72" />
      <Skeleton className="h-64 rounded-xl" />
    </div>
  )
}

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageFallback />}>{children}</Suspense>
}

/** Blocks the shell until the session is known, then redirects if anonymous. */
function RequireAuth() {
  const status = useAuthStore((state) => state.status)
  const location = useLocation()

  if (status === 'unknown') {
    return (
      <div className="bg-background grid min-h-dvh place-items-center">
        <div className="flex flex-col items-center gap-3">
          <span className="bg-primary text-primary-foreground grid size-10 animate-pulse place-items-center rounded-xl font-bold">
            U
          </span>
          <p className="text-muted-foreground text-[12.5px]">Checking your session…</p>
        </div>
      </div>
    )
  }

  if (status === 'anonymous') {
    const next = `${location.pathname}${location.search}`
    return (
      <Navigate to={`/login${next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`} replace />
    )
  }

  return <AppShell />
}

/** Route-level permission guard. Renders 403 rather than redirecting, so the
 *  URL stays shareable and the reason is explicit. */
function RequirePermission({ anyOf }: { anyOf: PermissionKey[] }) {
  const allowed = useAuthStore((state) => {
    if (state.user?.is_superuser) return true
    return anyOf.some((permission) => state.permissions.has(permission))
  })

  return allowed ? <Outlet /> : <ForbiddenPage permission={anyOf[0]} />
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage />, errorElement: <RouteErrorPage /> },
  {
    path: '/',
    element: <RequireAuth />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        element: <RequirePermission anyOf={[Permission.DASHBOARD_VIEW]} />,
        children: [
          {
            index: true,
            element: (
              <Lazy>
                <DashboardPage />
              </Lazy>
            ),
          },
          {
            path: 'activity',
            element: (
              <Lazy>
                <ActivityPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: (
          <RequirePermission anyOf={[Permission.TICKETS_VIEW, Permission.TICKETS_VIEW_ALL]} />
        ),
        children: [
          {
            path: 'tickets',
            element: (
              <Lazy>
                <TicketsPage />
              </Lazy>
            ),
          },
          {
            path: 'tickets/:id',
            element: (
              <Lazy>
                <TicketDetailPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.BUGS_VIEW]} />,
        children: [
          {
            path: 'bugs',
            element: (
              <Lazy>
                <BugsPage />
              </Lazy>
            ),
          },
          {
            path: 'bugs/:id',
            element: (
              <Lazy>
                <BugDetailPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.ROADMAP_VIEW]} />,
        children: [
          {
            path: 'roadmap',
            element: (
              <Lazy>
                <RoadmapPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.STATUS_PAGE_VIEW]} />,
        children: [
          {
            path: 'status-page',
            element: (
              <Lazy>
                <StatusPagePage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.TESTERS_VIEW]} />,
        children: [
          {
            path: 'beta-test',
            element: (
              <Lazy>
                <BetaTestPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.WIKI_EDIT, Permission.WIKI_MANAGE]} />,
        children: [
          {
            path: 'wiki',
            element: (
              <Lazy>
                <WikiConsolePage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.TICKETS_MANAGE_CATEGORIES]} />,
        children: [
          {
            path: 'tickets/categories',
            element: (
              <Lazy>
                <TicketCategoriesPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.TICKETS_MANAGE_FORMS]} />,
        children: [
          {
            path: 'tickets/forms',
            element: (
              <Lazy>
                <TicketFormsPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.TICKETS_MANAGE_PANELS]} />,
        children: [
          {
            path: 'tickets/panels',
            element: (
              <Lazy>
                <TicketPanelsPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.MEMBERS_VIEW]} />,
        children: [
          {
            path: 'members',
            element: (
              <Lazy>
                <MembersPage />
              </Lazy>
            ),
          },
          {
            path: 'members/:discordId',
            element: (
              <Lazy>
                <MemberDetailPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.MODERATION_VIEW]} />,
        children: [
          {
            path: 'moderation',
            element: (
              <Lazy>
                <ModerationQueuePage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.MODERATION_MANAGE_RULES]} />,
        children: [
          {
            path: 'moderation/rules',
            element: (
              <Lazy>
                <ModerationRulesPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.DISCIPLINE_VIEW]} />,
        children: [
          {
            path: 'discipline',
            element: (
              <Lazy>
                <DisciplineRegisterPage />
              </Lazy>
            ),
          },
          {
            path: 'discipline/sanctions',
            element: (
              <Lazy>
                <SanctionsPage />
              </Lazy>
            ),
          },
          {
            path: 'discipline/appeals',
            element: (
              <Lazy>
                <AppealsPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.DISCIPLINE_MANAGE]} />,
        children: [
          {
            path: 'discipline/setup',
            element: (
              <Lazy>
                <DisciplineCataloguePage />
              </Lazy>
            ),
          },
        ],
      },
      {
        // Everybody in the console reads the staff's rules; the page decides who writes.
        path: 'staff-rules',
        element: (
          <Lazy>
            <StaffRuleBookPage />
          </Lazy>
        ),
      },
      {
        element: <RequirePermission anyOf={[Permission.RULES_VIEW]} />,
        children: [
          {
            path: 'rules',
            element: (
              <Lazy>
                <RuleBookPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.WHITELIST_VIEW]} />,
        children: [
          {
            path: 'whitelist',
            element: (
              <Lazy>
                <WhitelistListPage />
              </Lazy>
            ),
          },
          {
            path: 'whitelist/:id',
            element: (
              <Lazy>
                <WhitelistDetailPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        // Positions come first: a bare `anyOf` route would let "positions" be
        // read as an application id by the detail route below it.
        element: <RequirePermission anyOf={[Permission.APPLICATIONS_MANAGE]} />,
        children: [
          {
            path: 'applications/positions',
            element: (
              <Lazy>
                <ApplicationPositionsPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.APPLICATIONS_VIEW]} />,
        children: [
          {
            path: 'applications',
            element: (
              <Lazy>
                <ApplicationListPage />
              </Lazy>
            ),
          },
          {
            path: 'applications/:id',
            element: (
              <Lazy>
                <ApplicationDetailPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.GIVEAWAYS_VIEW]} />,
        children: [
          {
            path: 'giveaways',
            element: (
              <Lazy>
                <GiveawayListPage />
              </Lazy>
            ),
          },
          {
            path: 'giveaways/:id',
            element: (
              <Lazy>
                <GiveawayDetailPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.POLLS_VIEW]} />,
        children: [
          {
            path: 'polls',
            element: (
              <Lazy>
                <PollListPage />
              </Lazy>
            ),
          },
          {
            path: 'polls/:id',
            element: (
              <Lazy>
                <PollDetailPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        // Setup first: a bare `anyOf` route would let "setup" be read as a
        // vehicle id by the detail route below it.
        element: <RequirePermission anyOf={[Permission.VEHICLES_MANAGE]} />,
        children: [
          {
            path: 'vehicles/setup',
            element: (
              <Lazy>
                <VehicleSetupPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.VEHICLES_MANAGE_PANEL]} />,
        children: [
          {
            path: 'vehicles/panel',
            element: (
              <Lazy>
                <VehiclePanelPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.GARAGES_VIEW]} />,
        children: [
          {
            // Before the detail route, for the reason Setup is: "garages" is not an id.
            path: 'vehicles/garages',
            element: (
              <Lazy>
                <GaragesPage />
              </Lazy>
            ),
          },
          {
            path: 'vehicles/garages/:id',
            element: (
              <Lazy>
                <GarageDetailPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.RENTALS_VIEW]} />,
        children: [
          {
            path: 'rentals',
            element: (
              <Lazy>
                <RentalsPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.CARWASH_VIEW]} />,
        children: [
          {
            path: 'carwash',
            element: (
              <Lazy>
                <CarWashPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.PLATES_VIEW]} />,
        children: [
          {
            path: 'plates',
            element: (
              <Lazy>
                <PlatesPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.VEHICLES_VIEW]} />,
        children: [
          {
            // Before the detail route, for the reason Setup is: "fuel" is not an id.
            path: 'vehicles/fuel',
            element: (
              <Lazy>
                <FuelPage />
              </Lazy>
            ),
          },
          {
            path: 'vehicles/stations',
            element: (
              <Lazy>
                <GasStationsPage />
              </Lazy>
            ),
          },
          {
            path: 'vehicles/workshops',
            element: (
              <Lazy>
                <WorkshopsPage />
              </Lazy>
            ),
          },
          {
            path: 'vehicles/dealerships',
            element: (
              <Lazy>
                <DealershipsPage />
              </Lazy>
            ),
          },
          {
            path: 'vehicles',
            element: (
              <Lazy>
                <VehicleListPage />
              </Lazy>
            ),
          },
          {
            path: 'vehicles/:id',
            element: (
              <Lazy>
                <VehicleDetailPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.CHARACTERS_VIEW]} />,
        children: [
          {
            path: 'characters',
            element: (
              <Lazy>
                <CharacterListPage />
              </Lazy>
            ),
          },
          {
            path: 'characters/:id',
            element: (
              <Lazy>
                <CharacterDetailPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.INVENTORY_VIEW]} />,
        children: [
          {
            path: 'inventory',
            element: (
              <Lazy>
                <InventoryPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.WEAPONS_VIEW]} />,
        children: [
          {
            path: 'weapons',
            element: (
              <Lazy>
                <WeaponListPage />
              </Lazy>
            ),
          },
          {
            path: 'weapons/:name',
            element: (
              <Lazy>
                <WeaponDetailPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.DOMAINS_VIEW]} />,
        children: [
          {
            path: 'domains',
            element: (
              <Lazy>
                <DomainsPage />
              </Lazy>
            ),
          },
          {
            path: 'domains/hosting',
            element: (
              <Lazy>
                <HostingPage />
              </Lazy>
            ),
          },
          {
            // Read by anybody who sees the registry, as Banking's setup is;
            // only `domains.manage` saves.
            path: 'domains/setup',
            element: (
              <Lazy>
                <DomainSetupPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.PHONES_VIEW]} />,
        children: [
          {
            path: 'phones',
            element: (
              <Lazy>
                <PhonesPage />
              </Lazy>
            ),
          },
          {
            path: 'phones/calls',
            element: (
              <Lazy>
                <PhoneCallsPage />
              </Lazy>
            ),
          },
          {
            path: 'phones/:uid',
            element: (
              <Lazy>
                <PhoneDetailPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.PHONES_MEDIA]} />,
        children: [
          {
            path: 'phones/media',
            element: (
              <Lazy>
                <PhoneMediaPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.TABLETS_VIEW]} />,
        children: [
          {
            path: 'tablets',
            element: (
              <Lazy>
                <TabletsPage />
              </Lazy>
            ),
          },
          {
            path: 'tablets/:uid',
            element: (
              <Lazy>
                <TabletDetailPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.TABLETS_MEDIA]} />,
        children: [
          {
            path: 'tablets/media',
            element: (
              <Lazy>
                <TabletMediaPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.MAIL_VIEW]} />,
        children: [
          {
            path: 'mail',
            element: (
              <Lazy>
                <MailPage />
              </Lazy>
            ),
          },
          {
            path: 'mail/accounts/:id',
            element: (
              <Lazy>
                <MailAccountPage />
              </Lazy>
            ),
          },
          {
            path: 'mail/services/:id',
            element: (
              <Lazy>
                <MailServicePage />
              </Lazy>
            ),
          },
          {
            // Read by anybody who sees the addresses, as the domains' setup is;
            // only `mail.manage` saves.
            path: 'mail/setup',
            element: (
              <Lazy>
                <MailSetupPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.NETWORK_VIEW]} />,
        children: [
          {
            path: 'network',
            element: (
              <Lazy>
                <TowersPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.POWER_VIEW]} />,
        children: [
          {
            path: 'power',
            element: (
              <Lazy>
                <PowerPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.SPEEDCAMERAS_VIEW]} />,
        children: [
          {
            path: 'speedcameras',
            element: (
              <Lazy>
                <SpeedCamerasPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.SCOOTERS_VIEW]} />,
        children: [
          {
            path: 'scooters',
            element: (
              <Lazy>
                <ScootersPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.DOORS_VIEW]} />,
        children: [
          {
            path: 'doors',
            element: (
              <Lazy>
                <DoorsPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.DRIVINGSCHOOL_VIEW]} />,
        children: [
          {
            path: 'drivingschool',
            element: (
              <Lazy>
                <DrivingSchoolPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.FIREARMS_VIEW]} />,
        children: [
          {
            path: 'firearms',
            element: (
              <Lazy>
                <FirearmsPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.INSURANCE_VIEW]} />,
        children: [
          {
            path: 'insurance',
            element: (
              <Lazy>
                <InsurancePage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.IMPOUND_VIEW]} />,
        children: [
          {
            path: 'impound',
            element: (
              <Lazy>
                <ImpoundPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.NEWS_VIEW]} />,
        children: [
          {
            path: 'weazel',
            element: (
              <Lazy>
                <NewsPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.NEWS_MANAGE]} />,
        children: [
          {
            path: 'weazel/articles/:id',
            element: (
              <Lazy>
                <ArticleEditorPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.INSTASHOT_VIEW]} />,
        children: [
          {
            path: 'instashot',
            element: (
              <Lazy>
                <InstashotPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.CHIRP_VIEW]} />,
        children: [
          {
            path: 'chirp',
            element: (
              <Lazy>
                <ChirpPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.STATIONS_VIEW]} />,
        children: [
          {
            path: 'stations',
            element: (
              <Lazy>
                <StationsPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.GRAFFITI_VIEW]} />,
        children: [
          {
            path: 'graffiti',
            element: (
              <Lazy>
                <GraffitiPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.LEDPANELS_VIEW]} />,
        children: [
          {
            path: 'ledpanels',
            element: (
              <Lazy>
                <LedPanelsPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.PINGPONG_VIEW]} />,
        children: [
          {
            path: 'pingpong',
            element: (
              <Lazy>
                <PingPongPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.DRUGS_VIEW]} />,
        children: [
          {
            path: 'drugs',
            element: (
              <Lazy>
                <DrugsPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.RACES_VIEW]} />,
        children: [
          {
            path: 'races',
            element: (
              <Lazy>
                <RacesPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.EMOTES_VIEW]} />,
        children: [
          {
            path: 'emotes',
            element: (
              <Lazy>
                <EmotesPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.VENDING_VIEW]} />,
        children: [
          {
            path: 'vending',
            element: (
              <Lazy>
                <VendingPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.PAWN_VIEW]} />,
        children: [
          {
            path: 'pawn',
            element: (
              <Lazy>
                <PawnPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.SHOPS_VIEW]} />,
        children: [
          {
            path: 'shops',
            element: (
              <Lazy>
                <ShopsPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.STYLE_VIEW]} />,
        children: [
          {
            path: 'style',
            element: (
              <Lazy>
                <StylePage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.CRAFTING_VIEW]} />,
        children: [
          {
            path: 'crafting',
            element: (
              <Lazy>
                <CraftingPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.CHESS_VIEW]} />,
        children: [
          {
            path: 'chess',
            element: (
              <Lazy>
                <ChessPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.BILLIARDS_VIEW]} />,
        children: [
          {
            path: 'billiards',
            element: (
              <Lazy>
                <BilliardsPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.COMPUTERS_VIEW]} />,
        children: [
          {
            path: 'desktops',
            element: (
              <Lazy>
                <DesktopsPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.DIRECTORY_VIEW]} />,
        children: [
          {
            path: 'directory',
            element: (
              <Lazy>
                <DirectoryPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.DATACENTER_VIEW]} />,
        children: [
          {
            path: 'datacenter',
            element: (
              <Lazy>
                <DatacenterPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.GROW_VIEW]} />,
        children: [
          {
            path: 'grow',
            element: (
              <Lazy>
                <GrowPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.CCTV_VIEW]} />,
        children: [
          {
            path: 'cctv',
            element: (
              <Lazy>
                <CamerasPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.BUSINESSES_VIEW]} />,
        children: [
          {
            path: 'businesses',
            element: (
              <Lazy>
                <BusinessesPage />
              </Lazy>
            ),
          },
          {
            path: 'businesses/registrations',
            element: (
              <Lazy>
                <BusinessRegistrationsPage />
              </Lazy>
            ),
          },
          {
            path: 'businesses/:id',
            element: (
              <Lazy>
                <BusinessDetailPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        // The shell creator's designs, under housing: static segments, so ranked above
        // the property detail's housing/:id.
        element: <RequirePermission anyOf={[Permission.SHELLS_VIEW]} />,
        children: [
          {
            path: 'housing/shells',
            element: (
              <Lazy>
                <ShellsPage />
              </Lazy>
            ),
          },
          {
            path: 'housing/shells/:id',
            element: (
              <Lazy>
                <ShellDetailPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.HOUSING_VIEW]} />,
        children: [
          {
            path: 'housing',
            element: (
              <Lazy>
                <PropertiesPage />
              </Lazy>
            ),
          },
          // Before the detail route: "furniture" is not an id.
          {
            path: 'housing/furniture',
            element: (
              <Lazy>
                <HousingFurniturePage />
              </Lazy>
            ),
          },
          {
            path: 'housing/interiors',
            element: (
              <Lazy>
                <HousingInteriorsPage />
              </Lazy>
            ),
          },
          {
            path: 'housing/settings',
            element: (
              <Lazy>
                <HousingSettingsPage />
              </Lazy>
            ),
          },
          {
            path: 'housing/:id',
            element: (
              <Lazy>
                <PropertyDetailPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.MDT_VIEW]} />,
        children: [
          {
            path: 'mdt',
            element: (
              <Lazy>
                <MdtPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.DISPATCH_VIEW, Permission.MDT_MANAGE]} />,
        children: [
          {
            path: 'dispatch',
            element: (
              <Lazy>
                <DispatchPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.MEDICAL_VIEW]} />,
        children: [
          {
            path: 'medical',
            element: (
              <Lazy>
                <MedicalPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.JAIL_VIEW]} />,
        children: [
          {
            path: 'jail',
            element: (
              <Lazy>
                <JailPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.ROBBERIES_VIEW]} />,
        children: [
          {
            path: 'robberies',
            element: (
              <Lazy>
                <RobberiesPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.BANKING_VIEW]} />,
        children: [
          {
            path: 'banking',
            element: (
              <Lazy>
                <BankingOverviewPage />
              </Lazy>
            ),
          },
          {
            path: 'banking/accounts',
            element: (
              <Lazy>
                <BankAccountsPage />
              </Lazy>
            ),
          },
          {
            path: 'banking/accounts/:id',
            element: (
              <Lazy>
                <BankAccountDetailPage />
              </Lazy>
            ),
          },
          {
            path: 'banking/ledger',
            element: (
              <Lazy>
                <BankLedgerPage />
              </Lazy>
            ),
          },
          {
            path: 'banking/loans',
            element: (
              <Lazy>
                <BankLoansPage />
              </Lazy>
            ),
          },
          {
            path: 'banking/skimmers',
            element: (
              <Lazy>
                <SkimmersPage />
              </Lazy>
            ),
          },
          {
            path: 'banking/labels',
            element: (
              <Lazy>
                <BankLabelsPage />
              </Lazy>
            ),
          },
          {
            path: 'banking/setup',
            element: (
              <Lazy>
                <BanksPage />
              </Lazy>
            ),
          },
          {
            // Where Setup used to live, for links saved before it moved.
            path: 'banking/banks',
            element: <Navigate to="/banking/setup" replace />,
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.QUESTS_VIEW]} />,
        children: [
          {
            path: 'quests',
            element: (
              <Lazy>
                <QuestsPage />
              </Lazy>
            ),
          },
          {
            path: 'quests/progress',
            element: (
              <Lazy>
                <QuestAssignmentsPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.BATTLEPASS_VIEW]} />,
        children: [
          {
            path: 'battlepass',
            element: (
              <Lazy>
                <BattlePassSeasonsPage />
              </Lazy>
            ),
          },
          {
            path: 'battlepass/seasons/new',
            element: (
              <Lazy>
                <BattlePassSeasonEditorPage />
              </Lazy>
            ),
          },
          {
            path: 'battlepass/seasons/:id',
            element: (
              <Lazy>
                <BattlePassSeasonEditorPage />
              </Lazy>
            ),
          },
          {
            path: 'battlepass/passes',
            element: (
              <Lazy>
                <BattlePassPassesPage />
              </Lazy>
            ),
          },
          {
            path: 'battlepass/payments',
            element: (
              <Lazy>
                <BattlePassPaymentsPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.STATUS_VIEW]} />,
        children: [
          {
            path: 'status',
            element: (
              <Lazy>
                <StatusPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.SKILLS_VIEW]} />,
        children: [
          {
            path: 'skills',
            element: (
              <Lazy>
                <SkillsPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.TALENTS_VIEW]} />,
        children: [
          {
            path: 'talents',
            element: (
              <Lazy>
                <TalentsPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.WEATHER_VIEW]} />,
        children: [
          {
            path: 'weather',
            element: (
              <Lazy>
                <WeatherPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.NPC_VIEW]} />,
        children: [
          {
            path: 'npc',
            element: (
              <Lazy>
                <NpcPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.CHAT_VIEW]} />,
        children: [
          {
            path: 'chat',
            element: (
              <Lazy>
                <GameChatPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.LIVE_MAP_VIEW]} />,
        children: [
          {
            path: 'live-map',
            element: (
              <Lazy>
                <LiveMapPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.BOT_ARCHITECT]} />,
        children: [
          {
            path: 'discord-ai',
            element: (
              <Lazy>
                <DiscordArchitectPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.VOICE_VIEW]} />,
        children: [
          {
            path: 'voice',
            element: (
              <Lazy>
                <VoicePage />
              </Lazy>
            ),
          },
          {
            path: 'voice/stats',
            element: (
              <Lazy>
                <StatsChannelsPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.STAFF_MANAGE_PANEL]} />,
        children: [
          {
            path: 'staff-panel',
            element: (
              <Lazy>
                <StaffPanelPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.ABSENCES_VIEW]} />,
        children: [
          {
            path: 'absences',
            element: (
              <Lazy>
                <AbsencesPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.ABSENCES_MANAGE_PANEL]} />,
        children: [
          {
            path: 'absences/panel',
            element: (
              <Lazy>
                <AbsencePanelPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.MESSAGES_VIEW]} />,
        children: [
          {
            path: 'messages',
            element: (
              <Lazy>
                <ScheduledMessagesPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.SETTINGS_VIEW]} />,
        children: [
          {
            path: 'settings',
            element: (
              <Lazy>
                <SettingsLayout />
              </Lazy>
            ),
            children: [
              {
                index: true,
                element: (
                  <Lazy>
                    <GeneralSettingsPage />
                  </Lazy>
                ),
              },
              {
                path: 'welcome',
                element: (
                  <Lazy>
                    <WelcomeSettingsPage />
                  </Lazy>
                ),
              },
              {
                path: 'moderation',
                element: (
                  <Lazy>
                    <ModerationSettingsPage />
                  </Lazy>
                ),
              },
              {
                path: 'nicknames',
                element: (
                  <Lazy>
                    <NicknameSettingsPage />
                  </Lazy>
                ),
              },
              {
                path: 'role-memory',
                element: (
                  <Lazy>
                    <RoleMemorySettingsPage />
                  </Lazy>
                ),
              },
              {
                path: 'arrival',
                element: (
                  <Lazy>
                    <ArrivalSettingsPage />
                  </Lazy>
                ),
              },
              {
                path: 'whitelist',
                element: (
                  <Lazy>
                    <WhitelistSettingsPage />
                  </Lazy>
                ),
              },
              {
                path: 'discipline',
                element: (
                  <Lazy>
                    <DisciplineSettingsPage />
                  </Lazy>
                ),
              },
              {
                path: 'permissions',
                element: (
                  <Lazy>
                    <PermissionsPage />
                  </Lazy>
                ),
              },
            ],
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.BOT_VIEW]} />,
        children: [
          {
            path: 'bot',
            element: (
              <Lazy>
                <BotPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        element: <RequirePermission anyOf={[Permission.AUDIT_VIEW]} />,
        children: [
          {
            path: 'audit',
            element: (
              <Lazy>
                <AuditPage />
              </Lazy>
            ),
          },
        ],
      },
      {
        path: 'account',
        element: (
          <Lazy>
            <AccountPage />
          </Lazy>
        ),
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
])
